import React, { useCallback, useRef } from 'react';
import { useCanvasAnimation } from '../../../hooks/useCanvasAnimation';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  age: number;
  life: number;
  color: string;
}

export function PlanetaryNebulaEjection() {
  const particlesRef = useRef<Particle[]>([]);
  const spawnAccumulatorRef = useRef<number>(0);

  const draw = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number, totalTime: number) => {
    const width = ctx.canvas.clientWidth;
    const height = ctx.canvas.clientHeight;

    const cx = width / 2;
    const cy = height / 2;

    // Clear background to #0F172A
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    const loopTime = totalTime % 10.0;

    // 1. UPDATE & DRAW EJECTED PARTICLES
    // Spawn particles during first 2 seconds of the loop (while Red Giant is visible)
    if (loopTime >= 0 && loopTime < 2.0) {
      spawnAccumulatorRef.current += deltaTime;
      const spawnRate = 20; // 20 particles per second
      const spawnInterval = 1 / spawnRate;
      
      while (spawnAccumulatorRef.current >= spawnInterval) {
        spawnAccumulatorRef.current -= spawnInterval;

        const angle = Math.random() * Math.PI * 2;
        const currentGiantRadius = 60 + Math.sin(totalTime * 3.0) * 5;
        const speed = 80 + Math.random() * 80;

        // Color matches closest ring
        // Ring 1 (blue) launched at 0, Ring 2 (red) at 1.5
        const color = loopTime >= 1.5 ? '#F87171' : '#60A5FA';

        particlesRef.current.push({
          x: cx + Math.cos(angle) * currentGiantRadius,
          y: cy + Math.sin(angle) * currentGiantRadius,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          age: 0,
          life: 1.0,
          color
        });
      }
    } else {
      spawnAccumulatorRef.current = 0;
    }

    // Update and draw particles
    particlesRef.current = particlesRef.current.filter((p) => {
      p.age += deltaTime;
      if (p.age >= p.life) return false;

      p.x += p.vx * deltaTime;
      p.y += p.vy * deltaTime;

      const opacity = 1 - p.age / p.life;
      ctx.fillStyle = p.color;
      ctx.globalAlpha = opacity;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
      ctx.fill();
      return true;
    });
    ctx.globalAlpha = 1.0; // reset transparency

    // 2. DRAW NEBULA RINGS
    // Define rings: launchTime, colorHex
    const rings = [
      { launch: 0.0, color: '#60A5FA' }, // Oxygen (blue)
      { launch: 1.5, color: '#F87171' }, // Hydrogen (red/pink)
      { launch: 3.0, color: '#34D399' }  // Nitrogen (green)
    ];

    rings.forEach((ring) => {
      const duration = 4.0;
      if (loopTime >= ring.launch && loopTime < ring.launch + duration) {
        const p = (loopTime - ring.launch) / duration; // 0 to 1
        const radius = p * 200;
        const opacity = p < 0.7 ? 1 : (1 - (p - 0.7) / 0.3);
        const lineWidth = 4 - p * 3;

        ctx.strokeStyle = ring.color;
        ctx.lineWidth = lineWidth;
        ctx.globalAlpha = opacity;
        
        // Add subtle radial glow using shadow API
        ctx.shadowColor = ring.color;
        ctx.shadowBlur = 8;

        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.stroke();

        // Reset shadow
        ctx.shadowBlur = 0;
      }
    });
    ctx.globalAlpha = 1.0; // reset transparency

    // 3. DRAW RED GIANT (Only visible first 2 seconds)
    if (loopTime >= 0 && loopTime < 2.0) {
      // Fade out envelope as t approaches 2.0
      let giantOpacity = 0.4;
      if (loopTime > 1.8) {
        giantOpacity = 0.4 * (1 - (loopTime - 1.8) / 0.2);
      }

      const currentRadius = 60 + Math.sin(totalTime * 3.0) * 5;
      
      const rgGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, currentRadius);
      rgGlow.addColorStop(0, `rgba(239, 68, 68, ${giantOpacity * 1.5})`);
      rgGlow.addColorStop(0.6, `rgba(220, 38, 38, ${giantOpacity})`);
      rgGlow.addColorStop(1, 'rgba(220, 38, 38, 0)');
      
      ctx.fillStyle = rgGlow;
      ctx.beginPath();
      ctx.arc(cx, cy, currentRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    // 4. DRAW WHITE DWARF (Always drawn at center)
    const pulseRadius = 8 + Math.sin(totalTime * 3.0) * 1.5;
    const wdGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, pulseRadius * 2.5);
    wdGlow.addColorStop(0, '#FFFFFF');
    wdGlow.addColorStop(0.2, '#60A5FA');
    wdGlow.addColorStop(1, 'rgba(96, 165, 250, 0)');
    
    ctx.fillStyle = wdGlow;
    ctx.beginPath();
    ctx.arc(cx, cy, pulseRadius * 2.5, 0, Math.PI * 2);
    ctx.fill();

    // White dwarf core sphere
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.arc(cx, cy, 4, 0, Math.PI * 2);
    ctx.fill();

    // White Dwarf Label
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('White Dwarf', cx, cy + 26);

    // 5. TEXT OVERLAYS
    // Top-left headers
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 14px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Planetary Nebula', 16, 28);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.font = '11px sans-serif';
    ctx.fillText('A misnomer — not planets! Just dying star gas.', 16, 46);

    // Loop info label bottom-left
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.font = '10px sans-serif';
    ctx.fillText(`Nebula Cycle: ${loopTime.toFixed(1)}s / 10.0s`, 16, height - 16);
  }, []);

  const canvasRef = useCanvasAnimation(draw, { height: 340 });

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}
